import { useState, useEffect, useCallback, createContext, useContext, ReactNode } from 'react';
import { X, ChevronRight, ChevronLeft, RotateCcw, HelpCircle, Sparkles } from 'lucide-react';
import { cn } from '../../lib/utils';

// ============================================================================
// TUTORIAL TYPES
// ============================================================================

export interface TutorialStep {
  id: string;
  target: string; // CSS selector for the target element
  title: string;
  content: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  action?: string; // Optional action hint (e.g., "Click the button")
  highlightPadding?: number;
}

export interface TutorialConfig {
  id: string;
  name: string;
  description: string;
  steps: TutorialStep[];
}

interface TutorialContextType {
  activeTutorial: string | null;
  currentStep: number;
  isActive: boolean;
  completedTutorials: string[];
  startTutorial: (tutorialId: string) => void;
  endTutorial: () => void;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (step: number) => void;
  markComplete: (tutorialId: string) => void;
  resetTutorial: (tutorialId: string) => void;
}

// ============================================================================
// TUTORIAL CONFIGURATIONS
// ============================================================================

export const TUTORIALS: Record<string, TutorialConfig> = {
  overview: {
    id: 'overview',
    name: 'Dashboard Overview',
    description: 'Get familiar with the main dashboard and crisis monitoring features',
    steps: [
      {
        id: 'active-crises',
        target: '[data-tutorial="active-crises"]',
        title: 'Active Crises Summary',
        content: 'This section shows all currently active crises with severity indicators. Click on any crisis card to view detailed information.',
        position: 'bottom',
      },
      {
        id: 'crisis-map',
        target: '[data-tutorial="crisis-map"]',
        title: 'Crisis Map',
        content: 'Interactive map showing crisis locations. Click markers to view details, zoom and pan to explore different regions.',
        position: 'left',
        action: 'Try clicking a map marker',
      },
      {
        id: 'priority-alerts',
        target: '[data-tutorial="priority-alerts"]',
        title: 'Priority Alerts',
        content: 'Critical alerts requiring immediate attention appear here. Review and acknowledge alerts as you assess situations.',
        position: 'left',
      },
      {
        id: 'quick-actions',
        target: '[data-tutorial="quick-actions"]',
        title: 'Quick Actions',
        content: 'Common actions like creating reports, requesting resources, and generating analysis are accessible here.',
        position: 'top',
      },
    ],
  },
  'crisis-map': {
    id: 'crisis-map',
    name: 'Crisis Map Navigation',
    description: 'Learn to navigate and use the interactive crisis map effectively',
    steps: [
      {
        id: 'map-filters',
        target: '[data-tutorial="map-filters"]',
        title: 'Map Filters',
        content: 'Use these dropdown filters to narrow down visible data by country, region, and crisis type.',
        position: 'bottom',
      },
      {
        id: 'data-layers',
        target: '[data-tutorial="data-layers"]',
        title: 'Data Layers',
        content: 'Toggle different data layers on and off. Show or hide cholera cases, flood risks, drought zones, and response teams.',
        position: 'right',
        action: 'Try toggling a layer',
      },
      {
        id: 'map-markers',
        target: '[data-tutorial="map-markers"]',
        title: 'Crisis Markers',
        content: 'Click on map markers to see detailed information about each crisis or team location. Colors indicate severity levels.',
        position: 'right',
        action: 'Click a marker for details',
      },
      {
        id: 'map-legend',
        target: '[data-tutorial="map-legend"]',
        title: 'Map Legend',
        content: 'Reference the legend to understand what different colors and symbols represent on the map.',
        position: 'top',
      },
      {
        id: 'map-actions',
        target: '[data-tutorial="map-actions"]',
        title: 'Map Actions',
        content: 'Generate situation briefings or create new alerts directly from the map interface.',
        position: 'top',
        action: 'Click to generate a briefing',
      },
    ],
  },
  knowledge: {
    id: 'knowledge',
    name: 'Knowledge Hub',
    description: 'Navigate the knowledge repository for protocols, contacts, and resources',
    steps: [
      {
        id: 'search-resources',
        target: '[data-tutorial="search-resources"]',
        title: 'Search Resources',
        content: 'Quickly find protocols, guides, and contacts using the search bar. Search by name, topic, or keyword.',
        position: 'bottom',
        action: 'Try searching for "cholera"',
      },
      {
        id: 'quick-access',
        target: '[data-tutorial="quick-access"]',
        title: 'Quick Access',
        content: 'These cards provide fast access to the most commonly needed resources: emergency protocols, contacts, guides, and training materials.',
        position: 'bottom',
      },
      {
        id: 'crisis-resources',
        target: '[data-tutorial="crisis-resources"]',
        title: 'Crisis-Specific Resources',
        content: 'Resources automatically filtered by currently active crises. Find relevant protocols and guidelines for ongoing situations.',
        position: 'right',
      },
      {
        id: 'key-contacts',
        target: '[data-tutorial="key-contacts"]',
        title: 'Key Contacts',
        content: 'Access contact information for emergency coordinators, cluster leads, and partner organizations.',
        position: 'left',
      },
      {
        id: 'document-library',
        target: '[data-tutorial="document-library"]',
        title: 'Document Library',
        content: 'Browse and download all available documents. Filter by category and sector to find what you need.',
        position: 'top',
      },
      {
        id: 'upload-document',
        target: '[data-tutorial="upload-document"]',
        title: 'Upload Documents',
        content: 'Share new resources with your team by uploading documents to the knowledge base.',
        position: 'bottom',
        action: 'Click to upload',
      },
    ],
  },
  detection: {
    id: 'detection',
    name: 'Crisis Detection',
    description: 'Learn how to monitor and respond to emerging crises',
    steps: [
      {
        id: 'search',
        target: '[data-tutorial="search"]',
        title: 'Search Alerts',
        content: 'Use the search bar to quickly find specific alerts by location, crisis type, or keywords.',
        position: 'bottom',
        action: 'Try searching for "Cholera"',
      },
      {
        id: 'filters',
        target: '[data-tutorial="filters"]',
        title: 'Filter Results',
        content: 'Narrow down alerts by region, crisis type, or severity level. Multiple filters can be combined.',
        position: 'bottom',
      },
      {
        id: 'alert-list',
        target: '[data-tutorial="alert-list"]',
        title: 'Active Alerts',
        content: 'View all active crisis alerts. Each card shows the severity, location, affected population, and time since detection.',
        position: 'right',
      },
      {
        id: 'acknowledge',
        target: '[data-tutorial="acknowledge"]',
        title: 'Acknowledge Alerts',
        content: 'Click "Acknowledge" to confirm you\'ve reviewed an alert. This helps track response progress.',
        position: 'left',
        action: 'Try acknowledging an alert',
      },
      {
        id: 'create-alert',
        target: '[data-tutorial="create-alert"]',
        title: 'Create New Alert',
        content: 'Manually create alerts for situations not automatically detected. Fill in location, severity, and description.',
        position: 'bottom',
        action: 'Click to open the alert creation form',
      },
      {
        id: 'alert-rules',
        target: '[data-tutorial="alert-rules"]',
        title: 'Alert Rules',
        content: 'Configure automated detection rules. Toggle rules on/off and set thresholds for different crisis types.',
        position: 'top',
      },
    ],
  },
  analysis: {
    id: 'analysis',
    name: 'AI Analysis',
    description: 'Generate AI-powered crisis analysis and recommendations',
    steps: [
      {
        id: 'generate',
        target: '[data-tutorial="generate"]',
        title: 'Generate Analysis',
        content: 'Click to generate an AI-powered analysis of the current crisis situation. This synthesizes data from multiple sources.',
        position: 'bottom',
        action: 'Click "Generate Analysis"',
      },
      {
        id: 'analysis-type',
        target: '[data-tutorial="analysis-type"]',
        title: 'Analysis Types',
        content: 'Switch between different analysis views: Situation Overview, Impact Assessment, and Recommendations.',
        position: 'bottom',
      },
      {
        id: 'data-sources',
        target: '[data-tutorial="data-sources"]',
        title: 'Data Sources',
        content: 'View which data sources contributed to this analysis. Toggle sources to customize the analysis scope.',
        position: 'left',
      },
      {
        id: 'confidence',
        target: '[data-tutorial="confidence"]',
        title: 'Confidence Score',
        content: 'The confidence percentage indicates how reliable the analysis is based on data quality and completeness.',
        position: 'top',
      },
      {
        id: 'export',
        target: '[data-tutorial="export"]',
        title: 'Export Analysis',
        content: 'Export the analysis as PDF or CSV for sharing with partners or inclusion in reports.',
        position: 'bottom',
        action: 'Click to download',
      },
      {
        id: 'history',
        target: '[data-tutorial="history"]',
        title: 'Historical Analysis',
        content: 'Access previous analyses to track how the situation has evolved over time.',
        position: 'top',
      },
    ],
  },
  operations: {
    id: 'operations',
    name: 'Operations Center',
    description: 'Coordinate response teams and resources',
    steps: [
      {
        id: 'teams-overview',
        target: '[data-tutorial="teams-overview"]',
        title: 'Response Teams',
        content: 'View all response teams, their current status, and deployment locations. Green indicates active deployment.',
        position: 'right',
      },
      {
        id: 'deploy-team',
        target: '[data-tutorial="deploy-team"]',
        title: 'Deploy Team',
        content: 'Assign teams to crisis locations. Select team type, destination, and mission parameters.',
        position: 'bottom',
        action: 'Click to open deployment form',
      },
      {
        id: 'resources',
        target: '[data-tutorial="resources"]',
        title: 'Resource Inventory',
        content: 'Track available resources across all locations. Monitor stock levels and expiration dates.',
        position: 'left',
      },
      {
        id: 'request-resources',
        target: '[data-tutorial="request-resources"]',
        title: 'Request Resources',
        content: 'Submit resource requests for specific locations. Specify quantities and priority level.',
        position: 'bottom',
        action: 'Click to request resources',
      },
      {
        id: 'timeline',
        target: '[data-tutorial="timeline"]',
        title: 'Activity Timeline',
        content: 'Track all operational activities in chronological order. Filter by team or activity type.',
        position: 'top',
      },
    ],
  },
  cash: {
    id: 'cash',
    name: 'Cash Assistance',
    description: 'Target beneficiaries and plan cash distributions',
    steps: [
      {
        id: 'map',
        target: '[data-tutorial="map"]',
        title: 'Targeting Map',
        content: 'Interactive map showing affected populations. Click markers to view detailed demographics and vulnerability data.',
        position: 'right',
        action: 'Click a map marker',
      },
      {
        id: 'demographics',
        target: '[data-tutorial="demographics"]',
        title: 'Demographics Panel',
        content: 'View detailed population breakdown including gender, age distribution, and vulnerability indicators.',
        position: 'left',
      },
      {
        id: 'market-tab',
        target: '[data-tutorial="market-tab"]',
        title: 'Market Analysis',
        content: 'Check market functionality, vendor availability, and price stability before choosing cash modality.',
        position: 'bottom',
      },
      {
        id: 'field-tab',
        target: '[data-tutorial="field-tab"]',
        title: 'Field Reports',
        content: 'Access field coordinator notes, accessibility information, and security considerations.',
        position: 'bottom',
      },
      {
        id: 'new-distribution',
        target: '[data-tutorial="new-distribution"]',
        title: 'Create Distribution',
        content: 'Launch the distribution wizard to plan a new cash transfer program. Select locations, amounts, and targeting criteria.',
        position: 'bottom',
        action: 'Click to start wizard',
      },
      {
        id: 'export-data',
        target: '[data-tutorial="export-data"]',
        title: 'Export Data',
        content: 'Download beneficiary data, targeting reports, and assessment summaries for offline use.',
        position: 'bottom',
      },
    ],
  },
};

// ============================================================================
// TUTORIAL CONTEXT
// ============================================================================

const TutorialContext = createContext<TutorialContextType | null>(null);

export function TutorialProvider({ children }: { children: ReactNode }) {
  const [activeTutorial, setActiveTutorial] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [completedTutorials, setCompletedTutorials] = useState<string[]>(() => {
    const saved = localStorage.getItem('completedTutorials');
    return saved ? JSON.parse(saved) : [];
  });

  const isActive = activeTutorial !== null;

  useEffect(() => {
    localStorage.setItem('completedTutorials', JSON.stringify(completedTutorials));
  }, [completedTutorials]);

  const startTutorial = useCallback((tutorialId: string) => {
    setActiveTutorial(tutorialId);
    setCurrentStep(0);
  }, []);

  const endTutorial = useCallback(() => {
    setActiveTutorial(null);
    setCurrentStep(0);
  }, []);

  const nextStep = useCallback(() => {
    if (!activeTutorial) return;
    const tutorial = TUTORIALS[activeTutorial];
    if (currentStep < tutorial.steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      // Tutorial complete
      markComplete(activeTutorial);
      endTutorial();
    }
  }, [activeTutorial, currentStep]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const goToStep = useCallback((step: number) => {
    setCurrentStep(step);
  }, []);

  const markComplete = useCallback((tutorialId: string) => {
    setCompletedTutorials(prev =>
      prev.includes(tutorialId) ? prev : [...prev, tutorialId]
    );
  }, []);

  const resetTutorial = useCallback((tutorialId: string) => {
    setCompletedTutorials(prev => prev.filter(id => id !== tutorialId));
  }, []);

  return (
    <TutorialContext.Provider
      value={{
        activeTutorial,
        currentStep,
        isActive,
        completedTutorials,
        startTutorial,
        endTutorial,
        nextStep,
        prevStep,
        goToStep,
        markComplete,
        resetTutorial,
      }}
    >
      {children}
    </TutorialContext.Provider>
  );
}

export function useTutorial() {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
}

// ============================================================================
// TUTORIAL OVERLAY COMPONENT
// ============================================================================

export function TutorialOverlay() {
  const { activeTutorial, currentStep, isActive, endTutorial, nextStep, prevStep } = useTutorial();
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  const tutorial = activeTutorial ? TUTORIALS[activeTutorial] : null;
  const step = tutorial?.steps[currentStep];

  useEffect(() => {
    if (!step) return;

    const findTarget = () => {
      const target = document.querySelector(step.target);
      if (target) {
        const rect = target.getBoundingClientRect();
        setTargetRect(rect);

        // Calculate tooltip position
        const padding = step.highlightPadding || 8;
        let top = 0;
        let left = 0;

        switch (step.position) {
          case 'top':
            top = rect.top - padding - 10;
            left = rect.left + rect.width / 2;
            break;
          case 'bottom':
            top = rect.bottom + padding + 10;
            left = rect.left + rect.width / 2;
            break;
          case 'left':
            top = rect.top + rect.height / 2;
            left = rect.left - padding - 10;
            break;
          case 'right':
            top = rect.top + rect.height / 2;
            left = rect.right + padding + 10;
            break;
          default:
            top = rect.bottom + padding + 10;
            left = rect.left + rect.width / 2;
        }

        setTooltipPosition({ top, left });
      }
    };

    findTarget();

    // Re-calculate on scroll or resize
    const handleUpdate = () => findTarget();
    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);

    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [step]);

  if (!isActive || !tutorial || !step) return null;

  const padding = step.highlightPadding || 8;

  return (
    <>
      {/* Backdrop with cutout */}
      <div className="fixed inset-0 z-[9998] pointer-events-none">
        <svg className="w-full h-full">
          <defs>
            <mask id="tutorial-mask">
              <rect width="100%" height="100%" fill="white" />
              {targetRect && (
                <rect
                  x={targetRect.left - padding}
                  y={targetRect.top - padding}
                  width={targetRect.width + padding * 2}
                  height={targetRect.height + padding * 2}
                  fill="black"
                />
              )}
            </mask>
          </defs>
          <rect
            width="100%"
            height="100%"
            fill="rgba(0, 0, 0, 0.6)"
            mask="url(#tutorial-mask)"
          />
        </svg>
      </div>

      {/* Highlight border */}
      {targetRect && (
        <div
          className="fixed z-[9999] pointer-events-none border-2 border-accent-500 transition-all duration-300"
          style={{
            top: targetRect.top - padding,
            left: targetRect.left - padding,
            width: targetRect.width + padding * 2,
            height: targetRect.height + padding * 2,
            boxShadow: '0 0 0 4px rgba(37, 99, 235, 0.2), 0 0 20px rgba(37, 99, 235, 0.3)',
          }}
        />
      )}

      {/* Tooltip */}
      <div
        className={cn(
          'fixed z-[10000] w-80 bg-white border border-gray-200 shadow-xl p-4 transition-all duration-300',
          step.position === 'top' && 'transform -translate-x-1/2 -translate-y-full',
          step.position === 'bottom' && 'transform -translate-x-1/2',
          step.position === 'left' && 'transform -translate-x-full -translate-y-1/2',
          step.position === 'right' && 'transform -translate-y-1/2'
        )}
        style={{
          top: tooltipPosition.top,
          left: tooltipPosition.left,
        }}
      >
        {/* Close button */}
        <button
          onClick={endTutorial}
          className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4 text-accent-500" />
          <span className="text-xs font-medium text-accent-500">
            Step {currentStep + 1} of {tutorial.steps.length}
          </span>
        </div>

        {/* Content */}
        <h4 className="text-sm font-semibold text-gray-900 mb-2">{step.title}</h4>
        <p className="text-sm text-gray-600 mb-3 leading-relaxed">{step.content}</p>

        {/* Action hint */}
        {step.action && (
          <div className="bg-accent-50 border border-accent-200 px-3 py-2 mb-3">
            <span className="text-xs font-medium text-accent-700">{step.action}</span>
          </div>
        )}

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-1.5 mb-3">
          {tutorial.steps.map((_, i) => (
            <div
              key={i}
              className={cn(
                'w-2 h-2 transition-colors',
                i === currentStep ? 'bg-accent-500' : i < currentStep ? 'bg-green-500' : 'bg-gray-200'
              )}
            />
          ))}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between pt-2 border-t border-gray-200">
          <button
            onClick={prevStep}
            disabled={currentStep === 0}
            className={cn(
              'px-3 py-1.5 text-xs font-medium flex items-center gap-1',
              currentStep === 0
                ? 'text-gray-300 cursor-not-allowed'
                : 'text-gray-600 hover:text-gray-900'
            )}
          >
            <ChevronLeft className="w-3 h-3" /> Back
          </button>

          <button
            onClick={endTutorial}
            className="text-xs text-gray-400 hover:text-gray-600"
          >
            Skip tutorial
          </button>

          <button
            onClick={nextStep}
            className="px-3 py-1.5 text-xs font-medium bg-accent-500 text-white hover:bg-accent-600 flex items-center gap-1"
          >
            {currentStep === tutorial.steps.length - 1 ? 'Finish' : 'Next'}
            <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      </div>
    </>
  );
}

// ============================================================================
// TUTORIAL START BUTTON
// ============================================================================

interface TutorialStartButtonProps {
  tutorialId: string;
  variant?: 'icon' | 'full';
  className?: string;
}

export function TutorialStartButton({ tutorialId, variant = 'full', className }: TutorialStartButtonProps) {
  const { startTutorial, completedTutorials, resetTutorial } = useTutorial();
  const tutorial = TUTORIALS[tutorialId];
  const isCompleted = completedTutorials.includes(tutorialId);

  if (!tutorial) return null;

  if (variant === 'icon') {
    return (
      <button
        onClick={() => startTutorial(tutorialId)}
        className={cn(
          'w-8 h-8 flex items-center justify-center border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors',
          className
        )}
        title={`Start ${tutorial.name} tutorial`}
      >
        <HelpCircle className="w-4 h-4 text-gray-500" />
      </button>
    );
  }

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <button
        onClick={() => startTutorial(tutorialId)}
        className="px-3 py-1.5 text-xs font-medium border border-accent-200 bg-accent-50 text-accent-700 hover:bg-accent-100 flex items-center gap-1.5 transition-colors"
      >
        <HelpCircle className="w-3.5 h-3.5" />
        {isCompleted ? 'Replay Tutorial' : 'Start Tutorial'}
      </button>
      {isCompleted && (
        <button
          onClick={() => resetTutorial(tutorialId)}
          className="p-1.5 text-gray-400 hover:text-gray-600"
          title="Reset tutorial progress"
        >
          <RotateCcw className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

// ============================================================================
// TUTORIAL WELCOME MODAL
// ============================================================================

interface TutorialWelcomeProps {
  tutorialId: string;
  open: boolean;
  onClose: () => void;
}

export function TutorialWelcome({ tutorialId, open, onClose }: TutorialWelcomeProps) {
  const { startTutorial, completedTutorials } = useTutorial();
  const tutorial = TUTORIALS[tutorialId];
  const isCompleted = completedTutorials.includes(tutorialId);

  if (!open || !tutorial) return null;

  const handleStart = () => {
    onClose();
    setTimeout(() => startTutorial(tutorialId), 300);
  };

  return (
    <div className="fixed inset-0 z-[9997] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white w-full max-w-md p-6 shadow-xl">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-accent-100 flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-accent-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{tutorial.name}</h3>
            <p className="text-sm text-gray-500">Interactive Tutorial</p>
          </div>
        </div>

        <p className="text-sm text-gray-600 mb-4 leading-relaxed">{tutorial.description}</p>

        <div className="bg-gray-50 p-3 mb-4">
          <div className="text-xs font-semibold text-gray-500 uppercase mb-2">What you'll learn:</div>
          <ul className="space-y-1">
            {tutorial.steps.slice(0, 4).map(step => (
              <li key={step.id} className="text-sm text-gray-600 flex items-center gap-2">
                <div className="w-1 h-1 bg-accent-500" />
                {step.title}
              </li>
            ))}
            {tutorial.steps.length > 4 && (
              <li className="text-sm text-gray-400">
                +{tutorial.steps.length - 4} more steps
              </li>
            )}
          </ul>
        </div>

        {isCompleted && (
          <div className="flex items-center gap-2 text-green-600 text-sm mb-4">
            <div className="w-4 h-4 bg-green-100 flex items-center justify-center">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </div>
            You've completed this tutorial
          </div>
        )}

        <div className="flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900"
          >
            Maybe Later
          </button>
          <button
            onClick={handleStart}
            className="px-4 py-2 text-sm font-medium bg-accent-500 text-white hover:bg-accent-600 flex items-center gap-2"
          >
            <Sparkles className="w-4 h-4" />
            {isCompleted ? 'Replay Tutorial' : 'Start Tutorial'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// TUTORIAL HUB - LIST ALL TUTORIALS
// ============================================================================

interface TutorialHubProps {
  open: boolean;
  onClose: () => void;
}

export function TutorialHub({ open, onClose }: TutorialHubProps) {
  const { startTutorial, completedTutorials, resetTutorial } = useTutorial();

  if (!open) return null;

  const tutorials = Object.values(TUTORIALS);

  return (
    <div className="fixed inset-0 z-[9997] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white w-full max-w-lg shadow-xl">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Learning Center</h3>
            <p className="text-sm text-gray-500">Interactive tutorials to help you get started</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-3 max-h-[60vh] overflow-y-auto">
          {tutorials.map(tutorial => {
            const isCompleted = completedTutorials.includes(tutorial.id);
            return (
              <div
                key={tutorial.id}
                className={cn(
                  'p-4 border transition-colors',
                  isCompleted ? 'border-green-200 bg-green-50' : 'border-gray-200 hover:border-gray-300'
                )}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-sm text-gray-900">{tutorial.name}</h4>
                      {isCompleted && (
                        <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-green-100 text-green-700 uppercase">
                          Completed
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mb-2">{tutorial.description}</p>
                    <div className="text-xs text-gray-400">{tutorial.steps.length} steps</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isCompleted && (
                      <button
                        onClick={() => resetTutorial(tutorial.id)}
                        className="p-2 text-gray-400 hover:text-gray-600"
                        title="Reset progress"
                      >
                        <RotateCcw className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => {
                        onClose();
                        setTimeout(() => startTutorial(tutorial.id), 300);
                      }}
                      className="px-3 py-1.5 text-xs font-medium bg-accent-500 text-white hover:bg-accent-600"
                    >
                      {isCompleted ? 'Replay' : 'Start'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">
              {completedTutorials.length} of {tutorials.length} completed
            </span>
            <div className="flex gap-1">
              {tutorials.map(t => (
                <div
                  key={t.id}
                  className={cn(
                    'w-2 h-2',
                    completedTutorials.includes(t.id) ? 'bg-green-500' : 'bg-gray-300'
                  )}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TutorialOverlay;
